import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { serversApi, companiesApi } from '../api'
import type { EdgeServer } from '../types'

interface Props {
  server: EdgeServer
  // 회사 배정은 전 회사 서버 관리자(Admin 또는 edge:servers)만 — 일반 Manager 는 회사 목록 API 도 403
  canAssignCompany: boolean
  onClose: () => void
}

export default function ServerEditModal({ server, canAssignCompany, onClose }: Props) {
  const qc = useQueryClient()
  const [form, setForm] = useState({
    ipAddress: server.ipAddress ?? '',
    memo: server.memo ?? '',
    externalUrl: server.externalUrl ?? '',
    companyId: server.companyId ?? '',
  })

  const { data: companies = [] } = useQuery({
    queryKey: ['companies'],
    queryFn: () => companiesApi.getAll().then(r => r.data),
    enabled: canAssignCompany,
  })

  const mutation = useMutation({
    mutationFn: () => serversApi.update(
      server.id,
      form.ipAddress || undefined,
      form.memo || undefined,
      form.externalUrl || undefined,
      form.companyId || undefined,
    ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['servers'] })
      onClose()
    },
  })

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
      <div className="bg-surface rounded-xl p-6 w-full max-w-md shadow-xl">
        <h3 className="font-semibold text-gray-800 mb-4">서버 정보 수정</h3>
        <div className="space-y-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1">서버명</label>
            <input value={server.name} disabled readOnly
              className="w-full border border-gray-200 bg-gray-100 text-gray-400 rounded-lg px-3 py-2 text-sm cursor-not-allowed" />
            <p className="text-xs text-red-500 mt-1">Edge의 이름은 Edge에서 수정 가능합니다</p>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">IP 주소</label>
            <input value={form.ipAddress}
              onChange={e => setForm({ ...form, ipAddress: e.target.value })}
              placeholder="예: 192.168.1.100"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
          </div>
          {canAssignCompany && (
            <div>
              <label className="block text-xs text-gray-500 mb-1">회사</label>
              <select value={form.companyId}
                onChange={e => setForm({ ...form, companyId: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                <option value="">미지정</option>
                {companies.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label className="block text-xs text-gray-500 mb-1">메모</label>
            <input value={form.memo}
              onChange={e => setForm({ ...form, memo: e.target.value })}
              placeholder="서버에 대한 메모"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">외부 URL <span className="text-gray-300">(Rust Client 바로가기)</span></label>
            <input value={form.externalUrl}
              onChange={e => setForm({ ...form, externalUrl: e.target.value })}
              placeholder="https://..."
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
          </div>
        </div>
        <div className="flex gap-2 mt-5">
          <button
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-accent-hover transition disabled:opacity-50">
            {mutation.isPending ? '저장 중…' : '저장'}
          </button>
          <button onClick={onClose}
            className="border border-gray-300 text-gray-600 px-4 py-2 rounded-lg text-sm">취소</button>
        </div>
      </div>
    </div>
  )
}
